export const TEST_DRIVE_FORM_BASE_URL =
    'https://www.hyundai.com/de/de/beratung-kauf/entdecken-und-erwerben/probefahrt.html';
export const OFFER_FORM_BASE_URL = 'https://www.hyundai.com/de/de/beratung-kauf/entdecken-und-erwerben/angebot.html';
export const BUSINESS_OFFER_FORM_BASE_URL =
    'https://www.hyundai.com/de/de/beratung-kauf/entdecken-und-erwerben/gewerbe-angebot.html';

const APPROVED_HYUNDAI_LEAD_FORM_BASE_URLS = [
    TEST_DRIVE_FORM_BASE_URL,
    OFFER_FORM_BASE_URL,
    BUSINESS_OFFER_FORM_BASE_URL,
] as const;

const APPROVED_HYUNDAI_LEAD_FORM_ORIGIN = new URL(TEST_DRIVE_FORM_BASE_URL).origin;

const APPROVED_HYUNDAI_LEAD_FORM_PATHS = new Set(
    APPROVED_HYUNDAI_LEAD_FORM_BASE_URLS.map((baseUrl) => new URL(baseUrl).pathname),
);

export function buildLeadFormUrl(options: {
    baseUrl: string;
    dealerSapId: string;
    campaign: string;
    campaignName: string;
    modelId?: string;
}): string {
    const { baseUrl, dealerSapId, campaign, campaignName, modelId } = options;
    const url = new URL(baseUrl);
    url.searchParams.set('dealer-id', dealerSapId);
    url.searchParams.set('campaign', campaign);
    url.searchParams.set('campaignName', campaignName);
    if (modelId) {
        url.searchParams.set('select-model-name', modelId);
    }
    return url.toString();
}

export function buildVaLeadFormUrls(options: {
    dealerSapId: string;
    campaignKmp: string;
    modelId: string;
    campaignName: string;
    testDriveUrlOverride?: string;
    offerUrlOverride?: string;
    disableTestdrive?: boolean;
}): { testDriveFormUrl: string; offerFormUrl: string; ctaFormUrl: string } {
    const testDriveFormUrl = buildLeadFormUrl({
        baseUrl: options.testDriveUrlOverride ?? TEST_DRIVE_FORM_BASE_URL,
        dealerSapId: options.dealerSapId,
        campaign: options.campaignKmp,
        campaignName: options.campaignName,
        modelId: options.modelId,
    });
    const offerFormUrl = buildLeadFormUrl({
        baseUrl: options.offerUrlOverride ?? OFFER_FORM_BASE_URL,
        dealerSapId: options.dealerSapId,
        campaign: options.campaignKmp,
        campaignName: options.campaignName,
        modelId: options.modelId,
    });

    return {
        testDriveFormUrl,
        offerFormUrl,
        ctaFormUrl: options.disableTestdrive ? offerFormUrl : testDriveFormUrl,
    };
}

export function getLeadFormQueryParam(url: string, param: string): string | null {
    return new URL(url).searchParams.get(param);
}

export function isHyundaiLeadFormUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        if (parsed.origin !== APPROVED_HYUNDAI_LEAD_FORM_ORIGIN) {
            return false;
        }
        if (!APPROVED_HYUNDAI_LEAD_FORM_PATHS.has(parsed.pathname)) {
            return false;
        }
        return parsed.searchParams.has('campaign') && parsed.searchParams.has('campaignName');
    } catch {
        return false;
    }
}

export function applyCampaignNameToLeadFormUrl(url: string, campaignName: string): string {
    try {
        const parsed = new URL(url);
        parsed.searchParams.set('campaignName', campaignName);
        return parsed.toString();
    } catch {
        return url;
    }
}

export function applySessionCampaignNameToLeadFormUrl(url: string, campaignName: string): string {
    if (!isHyundaiLeadFormUrl(url) || !campaignName.trim()) {
        return url;
    }
    return applyCampaignNameToLeadFormUrl(url, campaignName);
}

export function withVaLeadFormCampaignName<
    T extends { testDriveFormUrl: string; offerFormUrl: string; ctaFormUrl: string },
>(car: T, campaignName: string): T {
    return {
        ...car,
        testDriveFormUrl: applyCampaignNameToLeadFormUrl(car.testDriveFormUrl, campaignName),
        offerFormUrl: applyCampaignNameToLeadFormUrl(car.offerFormUrl, campaignName),
        ctaFormUrl: applyCampaignNameToLeadFormUrl(car.ctaFormUrl, campaignName),
    };
}
